import { StaffApiService, CommonApiService } from '@common/ApiService'
import JwtService from '@common/JwtService'
import AutoLogoutService from '@common/AutoLogoutService'
import { logoutUser, renewToken } from '@common/AuthServiceOidc'
import { providersActions } from '@common/GlobalProviders'

const AUTH_SUCCESS = 'AUTH_SUCCESS'
const AUTH_ERROR = 'AUTH_ERROR'
const AUTH_CLEAR = 'AUTH_CLEAR'
const LOGGING_IN_STATUS = 'LOGGING_IN_STATUS'
const USER_SUCCESS = 'USER_SUCCESS'
const DEVICE_SUCCESS = 'DEVICE_SUCCESS'
const USER_AGREEMENTS_SUCCESS = 'USER_AGREEMENTS_SUCCESS'
const UPDATE_USER_AGREEMENTS_MODALS = 'UPDATE_USER_AGREEMENTS_MODALS'

const handleAccountError = (commit, error) => {
  if (error.response?.status === 401) {
    setTimeout(() => {
      if (error.response.data.error_code === 'expired') {
        renewToken()
      } else {
        providersActions.addNotification({
          title: 'Error',
          text: 'This action cannot be performed, please try again later. If this issue still occurs, please contact the service desk.',
          type: 'error',
        })
        logoutUser()
      }
    }, 2000)
  } else {
    if (error.response?.data?.errors) {
      commit(AUTH_ERROR, error.response.data.errors)
    } else {
      commit(AUTH_ERROR, error)
    }
  }
}

export const state = {
  user: {},
  userRole: '',
  authToken: {},
  errors: {},
  isAuthenticated: !!JwtService.getToken(),
  loggingInStatus: 'complete',
  showPrivacy: false,
  showTerms: false,
}

export const getters = {
  currentUser(state) {
    return state.user
  },

  loggedIn(state) {
    return state.isAuthenticated
  },

  primaryUserRole(state) {
    return state.user?.role?.primary
  },

  secondaryUserRole(state) {
    return state.user.role.secondary
  },

  canConfirmOrReject(state) {
    // eslint-disable-next-line camelcase
    return state.user.role?.primary.unconfirmed_bookings_page
  },

  roleName(state) {
    return state.user.role.name
  },

  canCreateUser(state) {
    // eslint-disable-next-line camelcase
    return state.user.role?.secondary?.create_user ?? false
  },

  currentCrsCode(state) {
    // eslint-disable-next-line camelcase
    return state.user?.designated_station?.crs_code
  },
}

export const mutations = {
  [AUTH_ERROR](state, error) {
    state.errors = error
  },

  [AUTH_SUCCESS](state, data) {
    state.isAuthenticated = true
    state.authToken = data
    state.errors = {}
    try {
      JwtService.saveToken(state.authToken.access_token)
    } catch (e) {
      // access_token missing from response — auth headers won't be set
    }
    try {
      JwtService.saveIdToken(state.authToken.id_token)
    } catch (e) {
      // id_token missing from response — logout will fall back to signinRedirect
    }
    StaffApiService.setAuthorizationHeader()
    CommonApiService.setHeader()
    AutoLogoutService.setLogoutTime()
  },

  [DEVICE_SUCCESS](state, data) {
    StaffApiService.setDeviceIdHeader(data.id)
    window.localStorage.setItem('DEVICE_ID', data.id)
  },

  [LOGGING_IN_STATUS](state, data) {
    state.loggingInStatus = data
  },

  [USER_SUCCESS](state, data) {
    state.isAuthenticated = true
    state.user = data
    state.userRole = data.role.name
    state.errors = {}
  },

  [AUTH_CLEAR](state) {
    state.isAuthenticated = false
    state.user = {}
    state.userRole = ''
    state.errors = {}
    JwtService.destroyToken()
    JwtService.destroyIdToken()
    AutoLogoutService.destroyLogoutTime()
  },

  [USER_AGREEMENTS_SUCCESS](state, data) {
    state.errors = {}
    state.user.terms_accepted = data.terms_accepted
    state.user.privacy_accepted = data.privacy_accepted
  },

  [UPDATE_USER_AGREEMENTS_MODALS](state, { showPrivacy, showTerms }) {
    state.showPrivacy = showPrivacy
    state.showTerms = showTerms
  },
}

export const actions = {
  login({ commit }, credentials) {
    return new Promise((resolve) => {
      StaffApiService.post('v1/login', {
        username: credentials.username,
        password: credentials.password,
      })
        .then(({ data }) => {
          commit(AUTH_SUCCESS, data)
          resolve(data)
        })
        .catch(({ response }) => {
          commit(AUTH_ERROR, response)
        })
    })
  },

  loggingInStatus({ commit }, status) {
    commit(LOGGING_IN_STATUS, status)
  },

  logout({ commit }) {
    logoutUser()
  },

  processOneLoginResponse({ commit }, user) {
    commit(AUTH_SUCCESS, user)
    StaffApiService.patch('v1/onelogin/login_time').catch(({ response }) => commit(AUTH_ERROR, response))
  },

  createDevice({ commit }) {
    return new Promise((resolve, reject) => {
      StaffApiService.post('v1/devices', {
        os: 'web',
        app_type: 'staff',
      })
        .then(({ data }) => {
          commit(DEVICE_SUCCESS, data)
          resolve(true)
        })
        .catch(({ error }) => {
          commit(AUTH_ERROR, error)
          reject(error)
        })
    })
  },

  endShift({ commit }) {
    return new Promise((resolve, reject) => {
      StaffApiService.delete('v1/end_shift').catch(({ error }) => {
        commit(AUTH_ERROR, error)
        reject(error)
      })
    })
  },

  checkAuth({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        dispatch('useDevice')
        StaffApiService.setAuthorizationHeader()
        CommonApiService.setHeader()
        StaffApiService.query('v1/account')
          .then(({ data }) => {
            commit(USER_SUCCESS, data)
            resolve(data)
          })
          .catch((error) => {
            handleAccountError(commit, error)
            reject(error)
          })
      } else {
        logoutUser()
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(false)
      }
    })
  },

  queryAccount({ commit }) {
    StaffApiService.query('v1/account')
      .then(({ data }) => {
        commit(USER_SUCCESS, data)
      })
      .catch((error) => {
        handleAccountError(commit, error)
      })
  },

  acceptPrivacyPolicy({ commit }) {
    return StaffApiService.patch('v1/users/accept_privacy')
      .then(({ data }) => {
        commit(USER_AGREEMENTS_SUCCESS, data)
      })
      .catch(({ response }) => commit(AUTH_ERROR, response))
  },

  acceptTermsAndConditions({ commit }) {
    return StaffApiService.patch('v1/users/accept_terms')
      .then(({ data }) => {
        commit(USER_AGREEMENTS_SUCCESS, data)
      })
      .catch(({ response }) => commit(AUTH_ERROR, response))
  },

  updateUserAgreementsModals({ commit }, data) {
    commit(UPDATE_USER_AGREEMENTS_MODALS, data)
  },

  useDevice({ commit }) {
    const device = { id: window.localStorage.getItem('DEVICE_ID') || 'gotcha' }
    commit(DEVICE_SUCCESS, device)
  },
}
